import {
   Button,
   Card,
   Form,
   Input,
   Select,
   Tag,
   Upload,
   message,
   DatePicker,
   Tabs,
   Table,
   Image,
   Space,
   Modal
} from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import moment from 'moment'
import locale from 'antd/es/date-picker/locale/vi_VN'
import api from 'src/common/api'
import { Request2 } from 'src/common/Request2'
import { CONFIG_URL, DATE_TIME_FORMAT_CLIENT_NEW, PAGE_SIZE, ROLE } from 'src/helpers/constants'

const { TextArea } = Input
const { Option } = Select

const SEND_TYPE = {
   USER: 'user',
   ROLE: 'role'
}

const NOTI_TYPE_LABEL: Record<string, string> = {
   info: 'Thông tin',
   warning: 'Cảnh báo',
   order: 'Đơn hàng',
   fund: 'Tài chính'
}

const NotificationManagement = observer(() => {
   const [form] = Form.useForm()
   const [sendType, setSendType] = useState(SEND_TYPE.ROLE)
   const [loading, setLoading] = useState(false)
   const [emails, setEmails] = useState<string[]>([])
   const [emailInput, setEmailInput] = useState('')
   const [imageUrl, setImageUrl] = useState<string>('')
   const [uploading, setUploading] = useState(false)
   const [expiresAt, setExpiresAt] = useState<any>(null)

   const [activeTab, setActiveTab] = useState('create')
   const [listLoading, setListLoading] = useState(false)
   const [notifications, setNotifications] = useState<{ rows: any[]; count: number; offset: number }>({
      rows: [],
      count: 0,
      offset: 0
   })

   const [editForm] = Form.useForm()
   const [editVisible, setEditVisible] = useState(false)
   const [editingItem, setEditingItem] = useState<any>(null)
   const [editExpiresAt, setEditExpiresAt] = useState<any>(null)
   const [editImageUrl, setEditImageUrl] = useState<string>('')
   const [editUploading, setEditUploading] = useState(false)
   const [editLoading, setEditLoading] = useState(false)

   const isValidEmail = (email: string) => {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
   }

   const fetchNotifications = useCallback(async (currentPage: number = 0) => {
      try {
         setListLoading(true)
         const offset = currentPage * PAGE_SIZE
         const result = await Request2.getWithToken(api.notification.getAll, { offset, limit: PAGE_SIZE })
         if (result) {
            setNotifications({ rows: result.rows || [], count: result.count || 0, offset: result.offset || offset })
         }
      } catch (error) {
         // ignore
      } finally {
         setListLoading(false)
      }
   }, [])

   useEffect(() => {
      if (activeTab === 'list') {
         fetchNotifications(0)
      }
   }, [activeTab, fetchNotifications])

   const handleAddEmail = () => {
      const trimmed = emailInput.trim()
      if (!trimmed) return
      if (!isValidEmail(trimmed)) {
         message.warning('Email không hợp lệ')
         return
      }
      if (emails.includes(trimmed)) {
         message.warning('Email đã tồn tại')
         return
      }
      setEmails([...emails, trimmed])
      setEmailInput('')
   }

   const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
         e.preventDefault()
         handleAddEmail()
      }
   }

   const handleRemoveEmail = (emailToRemove: string) => {
      setEmails(emails.filter((e) => e !== emailToRemove))
   }

   const handleUploadImage = async (options: any) => {
      const { file } = options
      try {
         setUploading(true)
         const result = await Request2.uploadFile(file, api.upload.uploadFile)
         if (result?.data?.filename) {
            setImageUrl(result.data.filename)
            message.success('Upload ảnh thành công')
         }
      } catch (error) {
         message.error('Upload ảnh thất bại')
      } finally {
         setUploading(false)
      }
   }

   const handleSend = async (values: any) => {
      try {
         setLoading(true)
         const expiresAtISO = expiresAt ? moment(expiresAt).toISOString() : null
         if (sendType === SEND_TYPE.USER) {
            if (emails.length === 0) {
               message.warning('Vui lòng nhập ít nhất 1 email')
               return
            }
            await Request2.postWithToken(
               {
                  emails,
                  title: values.title,
                  description: values.description,
                  type: values.type || 'info',
                  image: imageUrl || null,
                  expiresAt: expiresAtISO
               },
               api.notification.send_to_user
            )
         } else {
            await Request2.postWithToken(
               {
                  toRole: values.toRole,
                  title: values.title,
                  description: values.description,
                  type: values.type || 'info',
                  image: imageUrl || null,
                  expiresAt: expiresAtISO
               },
               api.notification.send_to_role
            )
         }
         message.success('Gửi thông báo thành công!')
         form.resetFields()
         setEmails([])
         setEmailInput('')
         setImageUrl('')
         setExpiresAt(null)
      } catch (error) {
         message.error('Gửi thông báo thất bại!')
      } finally {
         setLoading(false)
      }
   }

   const handleOpenEdit = (record: any) => {
      setEditingItem(record)
      setEditExpiresAt(record?.expiresAt ? moment(record.expiresAt) : null)
      setEditImageUrl(record?.image || '')
      editForm.setFieldsValue({
         title: record?.title || '',
         description: record?.description || '',
         type: record?.type || 'info'
      })
      setEditVisible(true)
   }

   const handleCloseEdit = () => {
      setEditVisible(false)
      setEditingItem(null)
      setEditExpiresAt(null)
      setEditImageUrl('')
      editForm.resetFields()
   }

   const handleEditUploadImage = async (options: any) => {
      const { file } = options
      try {
         setEditUploading(true)
         const result = await Request2.uploadFile(file, api.upload.uploadFile)
         if (result?.data?.filename) {
            setEditImageUrl(result.data.filename)
            message.success('Upload ảnh thành công')
         }
      } catch (error) {
         message.error('Upload ảnh thất bại')
      } finally {
         setEditUploading(false)
      }
   }

   const handleUpdate = async (values: any) => {
      if (!editingItem?.id) return
      try {
         setEditLoading(true)
         const result = await Request2.patchWithToken(
            {
               title: values.title,
               description: values.description,
               type: values.type || 'info',
               image: editImageUrl || null,
               expiresAt: editExpiresAt ? moment(editExpiresAt).toISOString() : null
            },
            `${api.notification.update}/${editingItem.id}`
         )
         if (result) {
            message.success('Cập nhật thông báo thành công!')
            handleCloseEdit()
            const currentPage = notifications.offset / PAGE_SIZE
            fetchNotifications(currentPage)
         }
      } catch (error) {
         message.error('Cập nhật thông báo thất bại!')
      } finally {
         setEditLoading(false)
      }
   }

   const listColumns = [
      {
         title: 'Tiêu đề',
         dataIndex: 'title',
         key: 'title',
         width: 160,
         render: (value: string) => <span className="txtMax2Line">{value || '-'}</span>
      },
      {
         title: 'Nội dung',
         dataIndex: 'description',
         key: 'description',
         render: (value: string) => <span className="txtMax2Line">{value}</span>
      },
      {
         title: 'Loại',
         dataIndex: 'type',
         key: 'type',
         width: 110,
         render: (value: string) => NOTI_TYPE_LABEL[value] || value || '-'
      },
      {
         title: 'Gửi tới',
         key: 'target',
         width: 140,
         render: (_: any, record: any) =>
            record?.toRole ? `Role: ${record.toRole}` : record?.toUserId ? `User #${record.toUserId}` : '-'
      },
      {
         title: 'Ảnh',
         dataIndex: 'image',
         key: 'image',
         width: 80,
         render: (value: string) =>
            value ? (
               <Image src={`${CONFIG_URL.IMAGE_URL}/${value}`} width={48} height={48} style={{ objectFit: 'cover' }} />
            ) : (
               '-'
            )
      },
      {
         title: 'Ngày tạo',
         dataIndex: 'createdAt',
         key: 'createdAt',
         width: 140,
         render: (value: string) => (value ? moment(value).format(DATE_TIME_FORMAT_CLIENT_NEW) : '-')
      },
      {
         title: 'Hết hạn',
         dataIndex: 'expiresAt',
         key: 'expiresAt',
         width: 150,
         render: (value: string) => {
            if (!value) return <Tag>Không hết hạn</Tag>
            const expired = moment(value).isBefore(moment())
            return (
               <Space direction="vertical" size={0}>
                  <span>{moment(value).format(DATE_TIME_FORMAT_CLIENT_NEW)}</span>
                  <Tag color={expired ? 'red' : 'green'}>{expired ? 'Đã hết hạn' : 'Còn hiệu lực'}</Tag>
               </Space>
            )
         }
      },
      {
         title: 'Thao tác',
         key: 'action',
         width: 90,
         fixed: 'right' as 'right',
         render: (_: any, record: any) => (
            <Button type="link" onClick={() => handleOpenEdit(record)}>
               Sửa
            </Button>
         )
      }
   ]

   const renderCreateForm = () => (
      <Card title="Gửi thông báo" style={{ maxWidth: 600 }}>
         <Form form={form} layout="vertical" onFinish={handleSend}>
            <Form.Item label="Loại gửi">
               <Select value={sendType} onChange={(val) => setSendType(val)}>
                  <Option value={SEND_TYPE.ROLE}>Gửi theo Role</Option>
                  <Option value={SEND_TYPE.USER}>Gửi cho User cụ thể</Option>
               </Select>
            </Form.Item>

            {sendType === SEND_TYPE.USER ? (
               <Form.Item label="Email người nhận" required>
                  <div
                     style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: 6,
                        padding: '4px 8px',
                        minHeight: 40,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 4,
                        alignItems: 'center'
                     }}
                  >
                     {emails.map((email) => (
                        <Tag key={email} closable onClose={() => handleRemoveEmail(email)} style={{ marginBottom: 2 }}>
                           {email}
                        </Tag>
                     ))}
                     <Input
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyDown={handleEmailKeyDown}
                        onBlur={handleAddEmail}
                        placeholder="Nhập email rồi nhấn Enter"
                        bordered={false}
                        style={{ flex: 1, minWidth: 150 }}
                     />
                  </div>
               </Form.Item>
            ) : (
               <Form.Item label="Role nhận" name="toRole" rules={[{ required: true, message: 'Vui lòng chọn role' }]}>
                  <Select placeholder="Chọn role">
                     <Option value={ROLE.ADMIN}>Admin</Option>
                     <Option value={ROLE.SELLER}>Seller</Option>
                     <Option value={ROLE.FULFILLMENT}>Fulfillment</Option>
                     <Option value={ROLE.MANAGER_ORDER}>Manager Order</Option>
                     <Option value={ROLE.MANAGER_STOCK}>Manager Stock</Option>
                     <Option value={ROLE.ACCOUNTANT}>Accountant</Option>
                     <Option value={ROLE.NCC}>NCC</Option>
                  </Select>
               </Form.Item>
            )}

            <Form.Item label="Tiêu đề" name="title">
               <Input placeholder="Tiêu đề thông báo (tuỳ chọn)" />
            </Form.Item>

            <Form.Item
               label="Nội dung"
               name="description"
               rules={[{ required: true, message: 'Vui lòng nhập nội dung thông báo' }]}
            >
               <TextArea rows={4} placeholder="Nội dung thông báo (hỗ trợ Markdown)" />
            </Form.Item>

            <Form.Item label="Hình ảnh đính kèm">
               <Upload
                  listType="picture-card"
                  showUploadList={false}
                  customRequest={handleUploadImage}
                  accept="image/*"
               >
                  {imageUrl ? (
                     <img
                        src={`${CONFIG_URL.IMAGE_URL}/${imageUrl}`}
                        alt="notification"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                     />
                  ) : (
                     <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>{uploading ? 'Đang tải...' : 'Upload'}</div>
                     </div>
                  )}
               </Upload>
               {imageUrl && (
                  <Button type="link" danger onClick={() => setImageUrl('')} style={{ padding: 0 }}>
                     Xoá ảnh
                  </Button>
               )}
            </Form.Item>

            <Form.Item label="Loại thông báo" name="type">
               <Select placeholder="Chọn loại" allowClear>
                  <Option value="info">Thông tin</Option>
                  <Option value="warning">Cảnh báo</Option>
                  <Option value="order">Đơn hàng</Option>
                  <Option value="fund">Tài chính</Option>
               </Select>
            </Form.Item>

            <Form.Item
               label="Thời gian hết hạn"
               extra="Sau thời điểm này, popup thông báo sẽ không tự động hiển thị nữa. Để trống nếu không hết hạn."
            >
               <DatePicker
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  locale={locale}
                  value={expiresAt}
                  onChange={(value) => setExpiresAt(value)}
                  style={{ width: '100%' }}
                  placeholder="Chọn thời gian hết hạn (tuỳ chọn)"
                  disabledDate={(current) => current && current < moment().startOf('day')}
               />
            </Form.Item>

            <Form.Item>
               <Button type="primary" htmlType="submit" loading={loading}>
                  Gửi thông báo
               </Button>
            </Form.Item>
         </Form>
      </Card>
   )

   const renderList = () => (
      <Card title="Danh sách thông báo đã gửi">
         <Table
            columns={listColumns}
            dataSource={notifications.rows}
            rowKey="id"
            loading={listLoading}
            scroll={{ x: 1000 }}
            pagination={{
               pageSize: PAGE_SIZE,
               current: notifications.offset / PAGE_SIZE + 1,
               total: notifications.count,
               showSizeChanger: false,
               onChange: (page) => fetchNotifications(page - 1)
            }}
         />
      </Card>
   )

   return (
      <div style={{ padding: 24 }}>
         <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <Tabs.TabPane tab="Tạo thông báo" key="create">
               {renderCreateForm()}
            </Tabs.TabPane>
            <Tabs.TabPane tab="Danh sách thông báo" key="list">
               {renderList()}
            </Tabs.TabPane>
         </Tabs>

         <Modal
            title="Sửa thông báo"
            visible={editVisible}
            onCancel={handleCloseEdit}
            onOk={() => editForm.submit()}
            okText="Lưu"
            cancelText="Huỷ"
            confirmLoading={editLoading}
            destroyOnClose
         >
            <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
               <Form.Item label="Tiêu đề" name="title">
                  <Input placeholder="Tiêu đề thông báo (tuỳ chọn)" />
               </Form.Item>

               <Form.Item
                  label="Nội dung"
                  name="description"
                  rules={[{ required: true, message: 'Vui lòng nhập nội dung thông báo' }]}
               >
                  <TextArea rows={4} placeholder="Nội dung thông báo (hỗ trợ Markdown)" />
               </Form.Item>

               <Form.Item label="Loại thông báo" name="type">
                  <Select placeholder="Chọn loại" allowClear>
                     <Option value="info">Thông tin</Option>
                     <Option value="warning">Cảnh báo</Option>
                     <Option value="order">Đơn hàng</Option>
                     <Option value="fund">Tài chính</Option>
                  </Select>
               </Form.Item>

               <Form.Item label="Hình ảnh đính kèm">
                  <Upload
                     listType="picture-card"
                     showUploadList={false}
                     customRequest={handleEditUploadImage}
                     accept="image/*"
                  >
                     {editImageUrl ? (
                        <img
                           src={`${CONFIG_URL.IMAGE_URL}/${editImageUrl}`}
                           alt="notification"
                           style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                        />
                     ) : (
                        <div>
                           <PlusOutlined />
                           <div style={{ marginTop: 8 }}>{editUploading ? 'Đang tải...' : 'Upload'}</div>
                        </div>
                     )}
                  </Upload>
                  {editImageUrl && (
                     <Button type="link" danger onClick={() => setEditImageUrl('')} style={{ padding: 0 }}>
                        Xoá ảnh
                     </Button>
                  )}
               </Form.Item>

               <Form.Item
                  label="Thời gian hết hạn"
                  extra="Sau thời điểm này, popup thông báo sẽ không tự động hiển thị nữa. Để trống nếu không hết hạn."
               >
                  <DatePicker
                     showTime
                     format="DD/MM/YYYY HH:mm"
                     locale={locale}
                     value={editExpiresAt}
                     onChange={(value) => setEditExpiresAt(value)}
                     style={{ width: '100%' }}
                     placeholder="Chọn thời gian hết hạn (tuỳ chọn)"
                  />
               </Form.Item>
            </Form>
         </Modal>
      </div>
   )
})

export default NotificationManagement
